import { useState, useEffect } from "react";
import {
  Avatar,
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import { getUserDetails } from "../../../core/storage/localStorage";
import { getUserNameShortform } from "../../../utils/utils";
import {
  ACCOUNT_URL,
  FEEDBACK_URL,
  SAVED_URL,
  WORKPLACE_URL,
} from "../../../utils/routes";
import { NavLink, useHistory } from "react-router-dom";
import {
  AccountCircleRounded,
  AdminPanelSettingsRounded,
  BookmarksRounded,
  FeedbackRounded,
  LogoutRounded,
  SettingsRounded,
} from "@mui/icons-material";
import { logout } from "../../../core/repo/accountRepo";
import { useDispatch } from "react-redux";
import { usePostHog } from "posthog-js/react";
import {
  LOGOUT_SUCCESS,
  OPEN_SETTINGS_MENU,
  CLOSE_SETTINGS_MENU,
} from "../../../utils/posthogEvents";
import { makeStyles } from "tss-react/mui";
import { LightModeRounded, DarkModeRounded } from "@mui/icons-material";
import { useThemeSettings } from "../../../theme/ThemeSettingsProvider";

const useStyles = makeStyles()((theme, { expanded }, classes) => ({
  activeNavItem: {
    background: expanded ? theme.palette.primary.lightBg : "none",
    [`& .${classes.labelText}`]: {
      fontWeight: 500,
      background: theme.palette.light,
      backgroundClip: "text",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
    },
    [`& .${classes.listItemIcon}`]: {
      background: !expanded ? theme.palette.primary.lightBg : "none",
    },
  },
  comingSoonLabel: {
    fontSize: 10,
    fontWeight: 400,
    background: theme.palette.light,
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    textAlign: "center",
    marginTop: 2,
  },
  collapsedMenu: {
    flexDirection: "column",
    paddingLeft: 0,
    paddingRight: 0,
    [`& .${classes.listItemIcon}`]: {
      minWidth: 24,
      padding: 4,
      borderRadius: 6,
    },
    [`& .${classes.label}`]: {
      fontSize: 11,
      width: 48,
      textOverflow: "ellipsis",
      textAlign: "center",
    },
  },
  label: {
    fontSize: 14,
  },
  labelText: {
    fontWeight: 500,
    background: theme.palette.light,
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  listItemIcon: {},
}));

function Settings({ expanded }) {
  const { mode, toggleMode } = useThemeSettings();
  const dispatch = useDispatch();
  const [user, setUser] = useState(() => getUserDetails());
  const history = useHistory();
  const { classes } = useStyles({ expanded });
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const posthog = usePostHog();

  const navItems = [
    {
      icon: <AccountCircleRounded />,
      label: "Account Settings",
      path: ACCOUNT_URL,
    },
    ...(user.role === "Admin"
      ? [
          {
            icon: <AdminPanelSettingsRounded />,
            label: "Workplace Settings",
            path: WORKPLACE_URL,
          },
        ]
      : []),
    {
      icon: <BookmarksRounded />,
      label: "Saved Answers",
      path: SAVED_URL,
    },
    {
      icon: <FeedbackRounded />,
      label: "Feedback",
      path: FEEDBACK_URL,
    },
  ];

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    posthog.capture(OPEN_SETTINGS_MENU);
  };

  const handleClose = () => {
    setAnchorEl(null);
    posthog.capture(CLOSE_SETTINGS_MENU);
  };

  useEffect(() => {
    const handleStorage = () => {
      const user = getUserDetails();
      setUser(user);
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const handleLogout = async () => {
    setIsLoading(true);
    try {
      await dispatch(logout(user["stytch_user_id"], user["session_jwt"]));
      posthog.capture(LOGOUT_SUCCESS);
      history.push({ pathname: "/login" });
    } catch (err) {
      history.push({ pathname: "/login" });
    }
    handleClose();
    setIsLoading(false);
  };

  const handleToggleMode = () => {
    toggleMode();
    handleClose();
  };

  return (
    <>
      <Box px={1.5} pb={1}>
        <MenuItem
          className={`${!expanded ? classes.collapsedMenu : null}`}
          sx={{
            padding: 1,
            borderRadius: 1,
            cursor: "pointer",
          }}
          onClick={handleClick}>
          <ListItemIcon classes={{ root: classes.listItemIcon }}>
            <SettingsRounded />
          </ListItemIcon>
          <Typography className={classes.label} noWrap color="textSecondary">
            Settings
          </Typography>
        </MenuItem>
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        slotProps={{
          paper: { sx: { borderRadius: 3, px: 1, py: 0 } },
        }}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}>
        {navItems.map((item) => (
          <NavLink
            to={item.path}
            key={item.path}
            style={{
              textDecoration: "none",
              color: theme.palette.text.secondary,
            }}>
            <MenuItem sx={{ py: 1, borderRadius: 2 }} onClick={handleClose}>
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText>
                <Typography variant="body2">{item.label}</Typography>
              </ListItemText>
            </MenuItem>
          </NavLink>
        ))}
        <MenuItem
          sx={{ py: 1, borderRadius: 2, color: theme.palette.text.secondary }}
          onClick={handleToggleMode}>
          <ListItemIcon>
            {mode === "dark" ? (
              <LightModeRounded fontSize="small" />
            ) : (
              <DarkModeRounded fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            <Typography variant="body2">
              Switch to {mode === "dark" ? "Light Mode" : "Dark Mode"}
            </Typography>
          </ListItemText>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={handleLogout} sx={{ py: 1, borderRadius: 2 }}>
          <ListItemIcon>
            <LogoutRounded color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.error.main }}>
            <Typography variant="body2">Sign Out</Typography>
          </ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}

export default Settings;
